/**
 * Attendance modal for a session.
 */
import * as React from 'react';
import { UserCheck, HeartHandshake, Users, GraduationCap, UserCircle, Check } from 'lucide-react';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { Button } from '@/components/forms/Button';
import { AttendanceRow } from '@/components/common/AttendanceRow';
import { updateParticipantAttendance } from '@/services/inperson/sessions.service';
import {
  buildAttendanceUpdateDtos,
  collectAttendanceValidationErrors,
  groupAttendanceParticipants,
  initialAttendanceData,
  setAttendanceParticipant,
} from '@/components/sessions/inperson/session-form-utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import type { SessionWithParticipants } from '@/types/inperson/session.types';
import { getVolunteerRoleLabel } from '@/types/user.types';
import { getRoleLabel, getRoleLabelPlural } from '@/utils/role-label-utils';
import type { ParticipantAttendance } from '@/types/attendance.types';

/** Props for {@link SessionAttendanceModal}. */
interface SessionAttendanceModalProps {
  open: boolean;
  onClose: () => void;
  session: SessionWithParticipants;
  onSuccess: () => void;
}

/** Attendance modal for a session. */
export const SessionAttendanceModal: React.FC<SessionAttendanceModalProps> = ({
  open,
  onClose,
  session,
  onSuccess,
}) => {
  const [attendanceData, setAttendanceData] = React.useState<Map<string, ParticipantAttendance>>(
    new Map(),
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [validationErrors, setValidationErrors] = React.useState<Map<string, string>>(new Map());

  React.useEffect(() => {
    if (open && session.participants) {
      setAttendanceData(initialAttendanceData(session.participants));
      setValidationErrors(new Map());
    }
  }, [open, session.participants]);

  /** Replace one participant entry in the local attendance map. */
  const handleParticipantChange = (participantId: string, value: ParticipantAttendance) => {
    setAttendanceData((prev) => setAttendanceParticipant(prev, participantId, value));
  };

  /** Absent rows need a justification — collect errors and block submit if any. */
  const validateForm = (): boolean => {
    const errors = collectAttendanceValidationErrors(attendanceData);
    setValidationErrors(errors);
    return errors.size === 0;
  };

  /** PATCH every participant attendance in parallel, then close. */
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error(getErrorMessage('requiredFields'));
      return;
    }

    setIsSubmitting(true);

    try {
      const updates = buildAttendanceUpdateDtos(session, attendanceData).map((item) =>
        updateParticipantAttendance(session.id, item.participantId, item.dto),
      );

      await Promise.all(updates);

      toast.success('Présences mises à jour avec succès');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating attendance:', error);
      toast.error(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Pairs vs leftover tutors/students (no pair row). */
  const { pairGroups, unmatchedTutors, unmatchedStudents } = groupAttendanceParticipants(session);
  const hasPairs = pairGroups.length > 0;
  const hasUnmatched = unmatchedTutors.length > 0 || unmatchedStudents.length > 0;

  React.useEffect(() => {
    setValidationErrors(collectAttendanceValidationErrors(attendanceData));
  }, [attendanceData]);

  const isFormValid = validationErrors.size === 0;

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      size="2xl"
      header={{
        preset: 'validation',
        icon: UserCheck,
        iconSize: 'md',
        title: 'Gestion des présences',
        titleSize: 'lg',
        titleWeight: 'semibold',
      }}
      footer={
        <div className="flex gap-3 justify-end w-full">
          <Button preset="modal-cancel" onClick={onClose} disabled={isSubmitting}>
            Annuler
          </Button>
          <Button
            variant="primary"
            icon={Check}
            iconPosition="left"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={isSubmitting || !isFormValid}
          >
            Valider les présences
          </Button>
        </div>
      }
    >
      <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
        {hasPairs && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <HeartHandshake className="w-5 h-5 text-[var(--color-2-nuance-1)]" />
              <h4 className="text-base font-semibold text-[var(--color-1-nuance-1)]">
                Binômes Élève-Tuteur
              </h4>
              <span
                className="text-xs font-bold px-2 py-0.5 rounded-full border"
                style={{
                  background: 'color-mix(in srgb, var(--color-2-nuance-1) 12%, transparent)',
                  color: 'var(--color-2-nuance-1)',
                  borderColor: 'var(--color-2-nuance-1)',
                }}
              >
                {pairGroups.length}
              </span>
            </div>

            <div className="space-y-4">
              {pairGroups.map(({ tutor, students }) => (
                <div
                  key={tutor.id}
                  className="rounded-lg border-2 p-4 space-y-3"
                  style={{
                    background: 'color-mix(in srgb, var(--color-2-nuance-1) 3%, transparent)',
                    borderColor: 'color-mix(in srgb, var(--color-2-nuance-1) 20%, transparent)',
                  }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <GraduationCap className="w-4 h-4 text-[var(--color-2-nuance-1)]" />
                    <span className="text-xs font-medium text-[var(--color-2-nuance-1)]">
                      {tutor.role === 'volunteer'
                        ? (getVolunteerRoleLabel(tutor.user) ?? getRoleLabel('volunteer'))
                        : getRoleLabel('tutor')}
                    </span>
                  </div>
                  {(() => {
                    const tutorData = attendanceData.get(tutor.id);
                    if (!tutorData) return null;
                    return (
                      <AttendanceRow
                        participant={tutor}
                        value={tutorData}
                        onChange={(value) => handleParticipantChange(tutor.id, value)}
                        error={validationErrors.get(tutor.id)}
                      />
                    );
                  })()}

                  <div className="flex items-center gap-2 mb-2 mt-4">
                    <UserCircle className="w-4 h-4 text-[var(--color-3-nuance-1)]" />
                    <span className="text-xs font-medium text-[var(--color-3-nuance-1)]">
                      {students.length === 1
                        ? getRoleLabel('student')
                        : getRoleLabelPlural('student')}
                    </span>
                  </div>
                  {students.map((student) => {
                    const studentData = attendanceData.get(student.id);
                    if (!studentData) return null;
                    return (
                      <AttendanceRow
                        key={student.id}
                        participant={student}
                        value={studentData}
                        onChange={(value) => handleParticipantChange(student.id, value)}
                        error={validationErrors.get(student.id)}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        {hasUnmatched && (
          <div>
            {hasPairs && (
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-[var(--color-1-nuance-1)]" />
                <h4 className="text-base font-semibold text-[var(--color-1-nuance-1)]">
                  Autres Participants
                </h4>
              </div>
            )}

            {unmatchedTutors.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <GraduationCap className="w-4 h-4 text-[var(--color-2-nuance-1)]" />
                  <span className="text-sm font-medium text-[var(--color-2-nuance-1)]">
                    tuteurs et Bénévoles
                  </span>
                  <span
                    className="text-xs font-bold px-2 py-0.5 rounded-full border"
                    style={{
                      background: 'color-mix(in srgb, var(--color-2-nuance-1) 12%, transparent)',
                      color: 'var(--color-2-nuance-1)',
                      borderColor: 'var(--color-2-nuance-1)',
                    }}
                  >
                    {unmatchedTutors.length}
                  </span>
                </div>
                <div className="space-y-3">
                  {unmatchedTutors.map((tutor) => {
                    const tutorData = attendanceData.get(tutor.id);
                    if (!tutorData) return null;
                    return (
                      <AttendanceRow
                        key={tutor.id}
                        participant={tutor}
                        value={tutorData}
                        onChange={(value) => handleParticipantChange(tutor.id, value)}
                        error={validationErrors.get(tutor.id)}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {unmatchedStudents.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <UserCircle className="w-4 h-4 text-[var(--color-3-nuance-1)]" />
                  <span className="text-sm font-medium text-[var(--color-3-nuance-1)]">Élèves</span>
                  <span
                    className="text-xs font-bold px-2 py-0.5 rounded-full border"
                    style={{
                      background: 'color-mix(in srgb, var(--color-3-nuance-1) 12%, transparent)',
                      color: 'var(--color-3-nuance-1)',
                      borderColor: 'var(--color-3-nuance-1)',
                    }}
                  >
                    {unmatchedStudents.length}
                  </span>
                </div>
                <div className="space-y-3">
                  {unmatchedStudents.map((student) => {
                    const studentData = attendanceData.get(student.id);
                    if (!studentData) return null;
                    return (
                      <AttendanceRow
                        key={student.id}
                        participant={student}
                        value={studentData}
                        onChange={(value) => handleParticipantChange(student.id, value)}
                        error={validationErrors.get(student.id)}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {!hasPairs && !hasUnmatched && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Users className="w-12 h-12 text-muted-foreground opacity-40 mb-3" />
            <p className="text-sm font-medium text-muted-foreground">
              Aucun participant dans cette séance
            </p>
          </div>
        )}
      </div>
    </BaseModal>
  );
};
